import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { inr } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { SectionShell, useLastVisit, Eyebrow } from "./_shared";
import { TrendingUp, TrendingDown, History } from "lucide-react";

// A change-story narrative band (not another KPI card).
export function SinceLastVisit({ canReports }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const prevVisit = useLastVisit(user?.tenant_id);
  const dash = useQuery({ queryKey: ["dashboard"], queryFn: async () => (await api.get("/dashboard")).data });

  const visitLabel = prevVisit
    ? new Date(prevVisit).toLocaleDateString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })
    : null;

  return (
    <div className="rounded-xl border border-pawkt-border bg-pawkt-soft p-5 sm:p-6" data-testid="since-last-visit">
      <SectionShell query={dash}>
        {(d) => {
          if (!prevVisit) {
            return (
              <div className="flex items-center gap-3" data-testid="slv-first">
                <History className="h-6 w-6 shrink-0 text-pawkt-primary" />
                <div>
                  <Eyebrow>Since You Last Visited</Eyebrow>
                  <p className="text-sm text-pawkt-text-secondary mt-0.5">We'll show you exactly what moved in your business each time you come back.</p>
                </div>
              </div>
            );
          }
          const rev = d.revenue || {}; const cu = d.customers || {};
          const up = (rev.delta_pct || 0) >= 0;
          const top = (d.by_service || [])[0];
          return (
            <div>
              <Eyebrow>Since You Last Visited · {visitLabel}</Eyebrow>
              <p className="font-display text-lg font-extrabold text-pawkt-text mt-1">Your business moved.</p>
              <div className="mt-3 flex flex-wrap items-stretch gap-3">
                <button onClick={() => navigate("/reports")}
                  className="flex items-center gap-2 rounded-2xl border border-pawkt-border-soft bg-white px-4 py-2.5 hover:shadow-card transition-all">
                  <span className={up ? "text-pawkt-success-deep" : "text-pawkt-emergency"}>{up ? <TrendingUp className="h-4 w-4" /> : <TrendingDown className="h-4 w-4" />}</span>
                  <span className="text-sm"><span className="font-extrabold text-pawkt-text">{up ? "+" : ""}{rev.delta_pct || 0}%</span> <span className="text-pawkt-text-secondary">revenue vs last month</span></span>
                </button>
                <div className="flex items-center gap-2 rounded-2xl border border-pawkt-border-soft bg-white px-4 py-2.5">
                  <span className="text-sm"><span className="font-extrabold text-pawkt-text">+{cu.new_this_month || 0}</span> <span className="text-pawkt-text-secondary">new customers</span></span>
                </div>
              </div>
              {top && top.revenue > 0 && (
                <p className="mt-3 text-xs text-pawkt-text-secondary">Most of the movement came from <span className="font-bold text-pawkt-text">{top.name}</span>.</p>
              )}
            </div>
          );
        }}
      </SectionShell>
    </div>
  );
}
